import { cloneDeep, isNumber, sum } from 'lodash';
import { getFramework } from '@/framework';
import {
    DEFAULT_FONT_FAMILY,
    LayerType,
    MIN_FONT_SIZE,
    type BaseCells,
    type Cell,
    type ITableLayer,
    type ITableLayerProps,
} from '@/version-types';
import { DEFAULT_TABLE_CELL__TEXT_FONT_SIZE, MIN_BASE_CELL_WIDTH, MIN_BORDER_WIDTH } from '../default-configs';
import { getCellStyle } from './cell-style';
import { getTableOuterInsets } from './geometry';
import { applyTableLayout, getTableColumnMinimumWidths, getTableMinimumHeight } from './table-layout';

export { getCellStyle } from './cell-style';

export const hoverAreaClassName = 'hoverArea';

export interface MinSizeInfoByScale {
    width: number;
    hMinSizes: number[];
}

/**
 * 获取下一帧缩放后的图层数据
 *
 * 缩放逻辑:
 * - 只缩放宽度时, 缩小宽度, 高度会自适应撑开
 * - 只缩放高度时, 缩小高度, 宽度不变
 * - 宽度和高度同时缩放时, 先走`只缩放宽`的逻辑, 再走`只缩放高`的逻辑
 */
export const getScaledTableLayer = ({
    currentActiveTableLayer,
    delta: { deltaOfWidth: expectDeltaOfWidth, deltaOfHeight: expectDeltaOfHeight },
    onLimit,
    minSizeInfo: minSizeInfoArg,
    shouldScaleAllAttr = false,
    onGetMinSizeInfo,
}: {
    currentActiveTableLayer: ITableLayer;
    /** 最初的baseCells, 用于缩放过程中按照原始比例进行恢复 */
    originalBaseCells: BaseCells;
    delta: {
        deltaOfWidth?: number;
        deltaOfHeight?: number;
    };
    onLimit?: (direction: 'horizontal' | 'vertical') => void;
    /** 若没传minSizeInfo, 则会计算minSizeInfo并回调 (主要是为了性能优化, 防止一直计算, 这样minSizeInfo就只会计算一次了) */
    minSizeInfo?: MinSizeInfoByScale;
    /** 是否缩放所有属性(fontSize, borderWidth等) */
    shouldScaleAllAttr?: boolean;
    onGetMinSizeInfo?: (minSizeInfo: MinSizeInfoByScale) => void;
}): ITableLayer => {
    if (!expectDeltaOfHeight && !expectDeltaOfWidth) {
        return currentActiveTableLayer;
    }

    const newTableLayer = cloneDeep(currentActiveTableLayer);
    if (shouldScaleAllAttr && isNumber(expectDeltaOfWidth)) {
        const scale = (currentActiveTableLayer.width + expectDeltaOfWidth) / currentActiveTableLayer.width;
        for (const styleId in newTableLayer.data.styles) {
            const style = newTableLayer.data.styles[styleId];
            if (style.fontSize) {
                style.fontSize = Math.max(MIN_FONT_SIZE, Math.round(style.fontSize * scale));
            }
            if (style.border) {
                Object.values(style.border).forEach((borderItem) => {
                    borderItem.width = Math.max(MIN_BORDER_WIDTH, Math.round(borderItem.width * scale));
                });
            }
        }
    }
    const {
        baseCells: { horizontal: horizontalSizes, vertical: verticalSizes },
    } = newTableLayer.data;
    const insets = getTableOuterInsets(newTableLayer.data);
    const hMinSizes =
        minSizeInfoArg && !shouldScaleAllAttr ? minSizeInfoArg.hMinSizes : getTableColumnMinimumWidths(newTableLayer);
    const minSizeInfo: MinSizeInfoByScale = {
        width: sum(hMinSizes) + insets.left + insets.right,
        hMinSizes,
    };
    if (!minSizeInfoArg || shouldScaleAllAttr) onGetMinSizeInfo?.(minSizeInfo);

    const newHorizontalSizes = isNumber(expectDeltaOfWidth)
        ? getNewSizes({
              currentSizes: horizontalSizes,
              deltaAllSize:
                  currentActiveTableLayer.width +
                  expectDeltaOfWidth -
                  insets.left -
                  insets.right -
                  sum(horizontalSizes),
              minAllSize: sum(hMinSizes),
              minSizes: hMinSizes,
          })
        : horizontalSizes;
    const minimumHeight = getTableMinimumHeight(newTableLayer, newHorizontalSizes);
    const newVerticalSizes = isNumber(expectDeltaOfHeight)
        ? getNewSizes({
              currentSizes: verticalSizes,
              deltaAllSize:
                  currentActiveTableLayer.height +
                  expectDeltaOfHeight -
                  insets.top -
                  insets.bottom -
                  sum(verticalSizes),
              minAllSize: sum(minimumHeight.verticalSizes),
              minSizes: minimumHeight.verticalSizes,
          })
        : verticalSizes;

    const limitDirection =
        isNumber(expectDeltaOfWidth) && currentActiveTableLayer.width + expectDeltaOfWidth <= minSizeInfo.width
            ? 'horizontal'
            : isNumber(expectDeltaOfHeight) &&
                currentActiveTableLayer.height + expectDeltaOfHeight <= minimumHeight.height
              ? 'vertical'
              : null;
    if (limitDirection) onLimit?.(limitDirection);

    newTableLayer.data.baseCells = {
        horizontal: newHorizontalSizes,
        vertical: newVerticalSizes,
    };
    const changedAxes =
        isNumber(expectDeltaOfWidth) && isNumber(expectDeltaOfHeight)
            ? 'both'
            : isNumber(expectDeltaOfWidth)
              ? 'horizontal'
              : 'vertical';
    return applyTableLayout(newTableLayer, { mode: 'interaction', changedAxes });

    /** 缩放后的sizes, 缩小时按等比例缩小, 放大时按等原始比例放大(目的是恢复原样) */
    function getNewSizes({
        currentSizes,
        deltaAllSize,
        minAllSize,
        minSizes,
    }: {
        currentSizes: number[];
        deltaAllSize: number;
        minAllSize: number;
        minSizes?: number[];
    }) {
        const minimumSizes = minSizes ?? currentSizes.map(() => MIN_BASE_CELL_WIDTH);
        const targetSize = Math.max(minAllSize, sum(currentSizes) + deltaAllSize);
        const sizes = [...currentSizes];
        let rest = targetSize - sum(sizes);
        if (rest >= 0) {
            const averageDelta = rest / sizes.length;
            return sizes.map((size) => size + averageDelta);
        }

        let activeIndexes = sizes.map((_, index) => index).filter((index) => sizes[index] > minimumSizes[index]);
        while (rest < -0.001 && activeIndexes.length) {
            const averageDelta = -rest / activeIndexes.length;
            let reduced = 0;
            activeIndexes.forEach((index) => {
                const delta = Math.min(averageDelta, sizes[index] - minimumSizes[index]);
                sizes[index] -= delta;
                reduced += delta;
            });
            if (reduced <= 0.001) break;
            rest += reduced;
            activeIndexes = activeIndexes.filter((index) => sizes[index] > minimumSizes[index] + 0.001);
        }
        return sizes;
    }
};

export const getActiveTableLayer = (frameworkIdentity: string): ITableLayer | null => {
    const framework = getFramework(frameworkIdentity);
    const activePickLayer = framework.service.frame.getActivePickLayer();
    if (activePickLayer?.layerType === LayerType.TABLE) return activePickLayer as ITableLayer;
    return null;
};

type FontItem = { fontFamily: string; text: string };

type TableLayerFontRun = FontItem & {
    fontSize: number;
    bold: boolean;
    italic: boolean;
};

export const collectTableLayerFonts = (tableLayerData: ITableLayerProps | ITableLayerProps<true>): FontItem[] => {
    const fontList = (tableLayerData.cells.flat().filter(Boolean) as Cell[])
        .map<FontItem>((c) => {
            const style = getCellStyle(c, tableLayerData);
            return {
                fontFamily: style.fontFamily ?? DEFAULT_FONT_FAMILY().name,
                text: c.content,
            };
        })
        // 重复的fontFamily进行合并
        .reduce<FontItem[]>((fontList, font) => {
            const targetFont = fontList.find((f) => f.fontFamily === font.fontFamily);
            if (targetFont) targetFont.text += font.text;
            else fontList.push(font);
            return fontList;
        }, []);
    return fontList.filter((f) => f.text);
};

export const collectTableLayerFontRuns = (
    tableLayerData: ITableLayerProps | ITableLayerProps<true>,
): TableLayerFontRun[] => {
    const fontRunMap = new Map<string, TableLayerFontRun>();

    for (const cell of tableLayerData.cells.flat()) {
        if (!cell?.content) continue;

        const style = getCellStyle(cell as Cell, tableLayerData);
        const fontRun: TableLayerFontRun = {
            fontFamily: style.fontFamily ?? DEFAULT_FONT_FAMILY().name,
            fontSize: style.fontSize ?? DEFAULT_TABLE_CELL__TEXT_FONT_SIZE,
            bold: style.bold ?? false,
            italic: style.italic ?? false,
            text: cell.content,
        };
        const key = JSON.stringify([fontRun.fontFamily, fontRun.fontSize, fontRun.bold, fontRun.italic]);
        const current = fontRunMap.get(key);
        if (current) {
            current.text += fontRun.text;
        } else {
            fontRunMap.set(key, fontRun);
        }
    }

    return [...fontRunMap.values()];
};
